import * as React from 'react'
import { useRouter } from '@tanstack/react-router'
import { Download, RefreshCw, Share, SquarePlus } from 'lucide-react'
import { reportInstalled } from '@/api/webpush'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useI18n } from '@/i18n'
import { applyUpdate, captureInstallPrompt, detectPlatform, envOf, installMode, promptInstall, registerServiceWorker, usePwa } from '@/lib/pwa'

/** Registers the service worker, keeps the install prompt and reports installed use (rollout signal). */
export function PwaBootstrap() {
  const router = useRouter()
  React.useEffect(() => {
    const stop = captureInstallPrompt()
    if (import.meta.env.PROD) void registerServiceWorker((url) => void router.history.push(url))
    const env = envOf()
    if (env.standalone) {
      try {
        if (!sessionStorage.getItem('dv.pwaReported')) {
          sessionStorage.setItem('dv.pwaReported', '1')
          void reportInstalled(detectPlatform(env)).catch(() => undefined)
        }
      } catch {
        /* storage unavailable */
      }
    }
    return stop
  }, [router])
  return <UpdateBanner />
}

/** «Доступна нова версія — оновити». */
export function UpdateBanner() {
  const { t } = useI18n()
  const { updateReady } = usePwa()
  if (!updateReady) return null
  return (
    <div
      role="status"
      data-testid="pwa-update"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto flex max-w-md items-center gap-3 rounded-[12px] border border-border bg-surface px-4 py-3 shadow-pop"
    >
      <RefreshCw className="size-4 shrink-0 text-brand-strong" aria-hidden />
      <span className="text-[13px] font-medium">{t('pwa.update')}</span>
      <Button size="sm" className="ml-auto" onClick={() => applyUpdate()}>
        {t('pwa.updateAction')}
      </Button>
    </div>
  )
}

/** Topbar button: the browser's install dialog, or iOS instructions. */
export function InstallButton() {
  const { t } = useI18n()
  const pwa = usePwa()
  const [ios, setIos] = React.useState(false)
  const mode = installMode(envOf(), !!pwa.installEvent)
  if (pwa.installed || mode === 'installed' || mode === 'none') return null
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="hidden sm:inline-flex"
        data-testid="pwa-install"
        title={t('pwa.installHint')}
        onClick={() => (mode === 'prompt' ? void promptInstall() : setIos(true))}
      >
        <Download /> <span className="hidden md:inline">{t('pwa.install')}</span>
      </Button>
      <IosInstallDialog open={ios} onOpenChange={setIos} />
    </>
  )
}

export function IosInstallDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useI18n()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('pwa.ios.title')} description={t('pwa.ios.lead')} size="sm">
        <ol className="flex flex-col gap-3 text-[13px]">
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-sunken text-[12px] font-semibold">1</span>
            {t('pwa.ios.step1')}
          </li>
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-sunken text-[12px] font-semibold">2</span>
            <span>
              {t('pwa.ios.step2')} <Share className="inline size-4 align-text-bottom" aria-hidden />
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-sunken text-[12px] font-semibold">3</span>
            <span>
              {t('pwa.ios.step3')} <SquarePlus className="inline size-4 align-text-bottom" aria-hidden />
            </span>
          </li>
        </ol>
        <p className="mt-4 rounded-[8px] bg-sunken px-3 py-2 text-[12.5px] text-muted">{t('pwa.ios.push')}</p>
      </DialogContent>
    </Dialog>
  )
}
